import { formatReadableDateShort, safeParseISO } from 'shared/util/datetime-fns';

import type { BoundingBox, CheckboxMap, CheckboxTree, Filters, FlexibleDate, GeoQuery, Month, NumberRange } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const monthNames = displayValues.months as Record<string, string>[];

export type FilterType = 'checkboxMap' | 'nestedCheckboxMap' | 'simple' | 'dateSpecific' | 'dateMonth' | 'geoQuery' | 'boundingBox';
export type FilterData = { id: string; value?: string | string[]; displayName: string; location: string; filterType: FilterType };
export type ResetCurrentFilterPayload = Pick<FilterData, 'location' | 'filterType' | 'value'>;

export const checkboxToFilterData = (checkboxMap: CheckboxMap, display: (name: string) => string, location: keyof Filters): FilterData[] =>
    Object.keys(checkboxMap).map((name) => ({
        id: `${location}-${name.replace(/\s/g, '')}`,
        value: name,
        displayName: display(name),
        location,
        filterType: 'checkboxMap',
    }));

const checkboxTreeDisplayNameFromValue = (value: string): string => {
    const destinationPath = value.split(';');
    return destinationPath.length > 2 ? `${destinationPath[destinationPath.length - 1]}, ${destinationPath[1]}` : destinationPath[1];
};

export const checkboxTreeToFilterData = (checkboxTree: CheckboxTree, path: string[] = [], pathEndIndex = 0): FilterData[] =>
    Object.entries(checkboxTree).reduce((acc: FilterData[], cur: [string, CheckboxTree], _index, arr) => {
        const newPath = [...path, cur[0]];
        const newPathEndIndex = arr.length <= 1 ? pathEndIndex : path.length;
        const filterDataList = checkboxTreeToFilterData(cur[1], newPath, newPathEndIndex);

        if (filterDataList.length > 0) {
            return acc.concat(filterDataList);
        }
        const filterData: FilterData = {
            id: `destinations-${cur[0].replace(/\s+/g, '')}`,
            value: newPath.slice(0, newPathEndIndex + 1),
            displayName: checkboxTreeDisplayNameFromValue(cur[0]),
            location: 'destinations',
            filterType: 'nestedCheckboxMap',
        };
        return acc.concat(filterData);
    }, []);

export const numberRangeToFilterData = (
    range: NumberRange | undefined,
    displayName: string,
    displayUnit: string,
    location: keyof Filters,
): FilterData[] => {
    if (!range) return [];

    const [min, max] = range;

    if (min === null && max === null) return [];

    const rangeText =
        min !== null && max !== null
            ? `${min} ${displayUnit} - ${max} ${displayUnit}`
            : min !== null
              ? `Fra ${min} ${displayUnit}`
              : `Til ${max} ${displayUnit}`;

    return [
        {
            displayName: `${displayName}: ${rangeText}`,
            id: location,
            location,
            filterType: 'simple',
        },
    ];
};

export const stringToFilterData = (string: string, display: (str: string) => string, location: keyof Filters): FilterData[] =>
    string
        ? [
              {
                  displayName: display(string),
                  id: location,
                  location,
                  filterType: 'simple',
              },
          ]
        : [];

export const datesToFilterData = (
    ignoredFilters: Filters['ignoredFilters'],
    departureDate?: FlexibleDate,
    returnDate?: FlexibleDate,
    travelMonths?: Month[],
    duration?: NumberRange,
): FilterData[] => {
    if (
        (ignoredFilters.includes('departureDate') || (!departureDate?.date && !returnDate?.date)) &&
        !ignoredFilters.includes('travelMonths')
    ) {
        const durationData: FilterData[] = duration
            ? [{ displayName: `Reiselengde: ${duration.join(' - ')} dager`, id: 'duration', location: 'duration', filterType: 'simple' }]
            : [];
        const travelMonthData: FilterData[] =
            Array.isArray(travelMonths) && travelMonths.length
                ? [
                      {
                          displayName: `Måneder: ${travelMonths.map((a) => monthNames[a.month - 1].full).join(', ')}`,
                          id: 'travelMonths',
                          location: 'travelMonths',
                          filterType: 'simple',
                      },
                  ]
                : [];

        return [...travelMonthData, ...durationData];
    }
    const parsedDepartureDate = safeParseISO(departureDate?.date);
    const parsedReturnDate = safeParseISO(returnDate?.date);

    return parsedDepartureDate && parsedReturnDate
        ? [
              {
                  displayName: `Fra ${formatReadableDateShort(parsedDepartureDate)} til ${formatReadableDateShort(parsedReturnDate)}`,
                  id: 'travelDates',
                  location: 'departureDate',
                  filterType: 'dateSpecific',
              },
          ]
        : [];
};

/**
 * Converts a GeoQuery to FilterData for display in CurrentFilters.
 */
export const geoQueryToFilterData = (geoQuery?: GeoQuery): FilterData[] => {
    if (!geoQuery) return [];

    return [
        {
            id: 'geoQuery',
            displayName: `Område: ${geoQuery.radius} km radius`,
            location: 'geoQuery',
            filterType: 'geoQuery',
        },
    ];
};

/**
 * Converts a BoundingBox to FilterData for display in CurrentFilters.
 * Uses the destinationLabel if available, otherwise shows a generic label.
 */
export const boundingBoxToFilterData = (boundingBox?: BoundingBox, destinationLabel?: string): FilterData[] => {
    if (!boundingBox) return [];

    return [
        {
            id: 'boundingBox',
            displayName: destinationLabel || 'Kartområde',
            location: 'boundingBox',
            filterType: 'boundingBox',
        },
    ];
};
